import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Shield, AlertTriangle, Send, Building2, Lock, Paperclip, X, Upload } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const categorias = [
  { value: "fraude", label: "Fraude", icon: "💰" },
  { value: "assedio", label: "Assédio", icon: "⚠️" },
  { value: "seguranca", label: "Segurança do Trabalho", icon: "🛡️" },
  { value: "corrupcao", label: "Corrupção", icon: "🔓" },
  { value: "outro", label: "Outro", icon: "📝" },
];

export default function DenunciaForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    categoria: "",
    descricao: "",
    data_fato: "",
    local: "",
    pessoas_envolvidas: "",
    aceita_lgpd: false,
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      const allowed = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'txt', 'mp3', 'mp4', 'wav'];
      if (!allowed.includes(ext)) {
        toast.error(`Arquivo ${file.name} não permitido`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`Arquivo ${file.name} muito grande (máx. 10MB)`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.aceita_lgpd) {
      toast.error("É necessário aceitar a política de privacidade");
      return;
    }

    setLoading(true);
    try {
      // First create the denuncia
      const response = await axios.post(`${API_URL}/denuncias`, formData);
      const { protocolo, senha } = response.data;

      // Then upload files if any
      if (files.length > 0) {
        for (const file of files) {
          const fileFormData = new FormData();
          fileFormData.append("protocolo", protocolo);
          fileFormData.append("senha", senha);
          fileFormData.append("file", file);
          
          try {
            await axios.post(`${API_URL}/denuncias/acompanhar/anexo`, fileFormData, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (err) {
            console.error("Error uploading file:", err);
          }
        }
      }

      navigate("/denuncia/confirmacao", { 
        state: { protocolo, senha } 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar denúncia");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.categoria !== "";
    if (step === 2) return formData.descricao.length >= 30;
    return formData.aceita_lgpd;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#1a1a2e] transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-[#1a1a2e]">CONTERP</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#1a1a2e] text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Lock className="w-4 h-4" />
            Canal Anônimo e Seguro
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1a1a2e] mb-4">
            Registrar Denúncia
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto">
            Sua identidade será completamente preservada. Não coletamos dados pessoais ou rastreamos IP.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-12">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-300 ${
                  step >= s
                    ? "bg-[#1a1a2e] text-white"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {s}
              </div>
              {s < 3 && (
                <div
                  className={`w-16 h-1 rounded-full transition-all duration-300 ${
                    step > s ? "bg-[#1a1a2e]" : "bg-slate-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
            {/* Step 1: Category */}
            {step === 1 && (
              <div className="animate-fade-in space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-[#1a1a2e] mb-2">
                    Tipo de Denúncia
                  </h2>
                  <p className="text-slate-600 text-sm mb-6">
                    Selecione a categoria que melhor descreve a situação
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {categorias.map((cat) => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => handleChange("categoria", cat.value)}
                      className={`p-6 rounded-xl border-2 text-left transition-all duration-200 hover:-translate-y-1 ${
                        formData.categoria === cat.value
                          ? "border-[#f5a623] bg-[#f5a623]/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`categoria-${cat.value}`}
                    >
                      <span className="text-2xl mb-2 block">{cat.icon}</span>
                      <span className="font-medium text-[#1a1a2e]">{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Details */}
            {step === 2 && (
              <div className="animate-fade-in space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-[#1a1a2e] mb-2">
                    Detalhes da Denúncia
                  </h2>
                  <p className="text-slate-600 text-sm mb-6">
                    Descreva o ocorrido com o máximo de detalhes possível
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="descricao" className="text-sm font-medium text-slate-700">
                      Descrição do Fato *
                    </Label>
                    <Textarea
                      id="descricao"
                      value={formData.descricao}
                      onChange={(e) => handleChange("descricao", e.target.value)}
                      placeholder="Descreva detalhadamente o que aconteceu, quando, onde e quem estava envolvido..."
                      className="mt-2 min-h-[150px]"
                      data-testid="descricao-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Mínimo de 30 caracteres ({formData.descricao.length}/30)
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="data_fato" className="text-sm font-medium text-slate-700">
                        Data do Fato
                      </Label>
                      <Input
                        id="data_fato"
                        type="date"
                        value={formData.data_fato}
                        onChange={(e) => handleChange("data_fato", e.target.value)}
                        className="mt-2"
                        data-testid="data-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="local" className="text-sm font-medium text-slate-700">
                        Local do Ocorrido
                      </Label>
                      <Input
                        id="local"
                        value={formData.local}
                        onChange={(e) => handleChange("local", e.target.value)}
                        placeholder="Ex: Escritório de Salvador, Sonda X..."
                        className="mt-2"
                        data-testid="local-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="pessoas" className="text-sm font-medium text-slate-700">
                      Pessoas Envolvidas
                    </Label>
                    <Textarea
                      id="pessoas"
                      value={formData.pessoas_envolvidas}
                      onChange={(e) => handleChange("pessoas_envolvidas", e.target.value)}
                      placeholder="Liste os nomes ou cargos das pessoas envolvidas (opcional)"
                      className="mt-2 min-h-[80px]"
                      data-testid="pessoas-input"
                    />
                  </div>

                  {/* File Upload */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700">
                      Anexos (opcional)
                    </Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Adicione documentos, imagens ou áudios que possam ajudar na investigação
                    </p>
                    <div className="mt-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        className="hidden"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.mp3,.mp4,.wav"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full border-dashed border-2 py-8 hover:border-[#f5a623]"
                        data-testid="btn-anexar"
                      >
                        <Upload className="w-5 h-5 mr-2 text-slate-400" />
                        <span className="text-slate-600">Clique para anexar arquivos</span>
                      </Button>
                      <p className="text-xs text-slate-400 mt-2 text-center">
                        PDF, DOC, XLS, imagens, áudio (máx. 10MB cada)
                      </p>
                    </div>

                    {/* File List */}
                    {files.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {files.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                          >
                            <Paperclip className="w-4 h-4 text-slate-400" />
                            <span className="flex-1 text-sm text-[#1a1a2e] truncate">
                              {file.name}
                            </span>
                            <span className="text-xs text-slate-500">
                              {(file.size / 1024).toFixed(0)} KB
                            </span>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              className="text-slate-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {step === 3 && (
              <div className="animate-fade-in space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-[#1a1a2e] mb-2">
                    Confirmação
                  </h2>
                  <p className="text-slate-600 text-sm mb-6">
                    Revise as informações e aceite os termos para enviar
                  </p>
                </div>

                {/* Summary */}
                <div className="bg-slate-50 rounded-lg p-6 space-y-4">
                  <div>
                    <span className="text-xs font-medium text-slate-500 uppercase">Categoria</span>
                    <p className="text-[#1a1a2e] font-medium">
                      {categorias.find(c => c.value === formData.categoria)?.label}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs font-medium text-slate-500 uppercase">Descrição</span>
                    <p className="text-[#1a1a2e] text-sm">{formData.descricao}</p>
                  </div>
                  {formData.data_fato && (
                    <div>
                      <span className="text-xs font-medium text-slate-500 uppercase">Data do Fato</span>
                      <p className="text-[#1a1a2e]">{formData.data_fato}</p>
                    </div>
                  )}
                  {formData.local && (
                    <div>
                      <span className="text-xs font-medium text-slate-500 uppercase">Local</span>
                      <p className="text-[#1a1a2e]">{formData.local}</p>
                    </div>
                  )}
                  {files.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-slate-500 uppercase">Anexos ({files.length})</span>
                      <div className="mt-1 space-y-1">
                        {files.map((file, i) => (
                          <p key={i} className="text-sm text-[#1a1a2e] flex items-center gap-2">
                            <Paperclip className="w-3 h-3" /> {file.name}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Privacy Notice */}
                <div className="bg-[#1a1a2e]/5 rounded-lg p-6">
                  <div className="flex gap-4">
                    <Shield className="w-6 h-6 text-[#1a1a2e] flex-shrink-0" />
                    <div>
                      <h3 className="font-semibold text-[#1a1a2e] mb-2">
                        Garantia de Anonimato
                      </h3>
                      <p className="text-slate-600 text-sm">
                        Ao enviar esta denúncia, você receberá um protocolo e senha únicos. 
                        Use-os para acompanhar o andamento do caso sem revelar sua identidade. 
                        Não armazenamos IP ou dados pessoais.
                      </p>
                    </div>
                  </div>
                </div>

                {/* LGPD Consent */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 pt-0.5">
                    <Checkbox
                      id="lgpd"
                      checked={formData.aceita_lgpd}
                      onCheckedChange={(checked) => handleChange("aceita_lgpd", checked)}
                      data-testid="lgpd-checkbox"
                    />
                  </div>
                  <Label htmlFor="lgpd" className="text-sm text-slate-600 cursor-pointer select-none">
                    Li e concordo com a{" "}
                    <Link to="/politica-privacidade" className="text-[#0EA5E9] hover:underline" target="_blank">
                      Política de Privacidade
                    </Link>
                    {" "}e autorizo o tratamento dos dados fornecidos conforme a LGPD.
                  </Label>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              {step > 1 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(step - 1)}
                  data-testid="btn-voltar"
                >
                  Voltar
                </Button>
              ) : (
                <div />
              )}

              {step < 3 ? (
                <Button
                  type="button"
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="bg-[#1a1a2e] hover:bg-[#1a1a2e]/90"
                  data-testid="btn-continuar"
                >
                  Continuar
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={!canProceed() || loading}
                  className="bg-[#f5a623] hover:bg-[#f5a623]/90 text-[#1a1a2e]"
                  data-testid="btn-enviar"
                >
                  {loading ? (
                    "Enviando..."
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Enviar Denúncia
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
